import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Menu,
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { company, navLinks, services } from "../../data/mock";

/* ---------- Reveal on scroll ---------- */
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${visible ? "reveal-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

/* ---------- Scroll to top on route change ---------- */
export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

/* ---------- Brand logo ---------- */
const BrandLogo = () => (
  <Link to="/" className="brand-logo" aria-label={company.name}>
    <span className="brand-main">
      Accent<span> Prestări</span>
    </span>
    <span className="brand-sub">contabilitate & resurse umane</span>
  </Link>
);

/* ---------- Header ---------- */
const Header = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const transparent = isHome && !scrolled;

  return (
    <header className={`site-header ${transparent ? "transparent" : ""}`}>
      <div className="header-inner">
        <BrandLogo />
        <nav className="main-nav" aria-label="Meniu principal">
          {navLinks.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <a
          href={`tel:${company.phoneHref}`}
          className="header-cta"
          aria-label={`Sună la ${company.phone}`}
          data-testid="header-phone-cta"
        >
          <Phone size={15} className="header-cta-icon" />
          <span>{company.phone}</span>
        </a>
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button className="mobile-menu-btn" aria-label="Deschide meniul">
              <Menu size={28} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] bg-white">
            <SheetTitle className="sr-only">Meniu de navigare</SheetTitle>
            <div className="brand-logo" style={{ marginTop: 8 }}>
              <span className="brand-main" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: "1.4rem", color: "var(--ce-teal)" }}>
                Accent<span style={{ color: "var(--ce-pink)" }}> Prestări</span>
              </span>
            </div>
            <nav className="drawer-nav" aria-label="Meniu mobil">
              {navLinks.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === "/"}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="drawer-cta">
              <a
                href={`tel:${company.phoneHref}`}
                className="btn-teal"
                style={{ textAlign: "center" }}
                onClick={() => setDrawerOpen(false)}
                data-testid="drawer-phone-cta"
              >
                <Phone size={15} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} />
                {company.phone}
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer className="site-footer">
    <div className="site-container">
      <div className="footer-grid">
        <div>
          <div className="footer-brand">
            Accent<span> Prestări</span>
          </div>
          <p className="footer-desc">
            Peste 30 de ani de experiență în contabilitate primară și
            resurse umane. Documentele se transmit digital, pe drive, iar
            comunicarea rămâne directă, în timp real.
          </p>
        </div>
        <div>
          <h4 className="footer-title">Link-uri utile</h4>
          <ul className="footer-links">
            {navLinks.map((l) => (
              <li key={l.path}>
                <Link to={l.path}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="footer-title">Servicii</h4>
          <ul className="footer-links">
            {services.slice(0, 6).map((s) => (
              <li key={s.id}>
                <Link to="/servicii">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="footer-title">Contact</h4>
          <div className="footer-contact-item">
            <MapPin size={17} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{company.address}</span>
          </div>
          <div className="footer-contact-item">
            <Phone size={17} style={{ flexShrink: 0, marginTop: 2 }} />
            <a href={`tel:${company.phoneHref}`}>{company.phone}</a>
          </div>
          <div className="footer-contact-item">
            <Mail size={17} style={{ flexShrink: 0, marginTop: 2 }} />
            <a href={`mailto:${company.email}`} className="footer-email-link">
              {company.email}
            </a>
          </div>
          <div className="footer-contact-item">
            <Clock size={17} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{company.schedule}</span>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>
          © {new Date().getFullYear()} {company.legalName} · CUI{" "}
          {company.cui} · Nr. Reg. Com. {company.regCom}
        </span>
        <span>Toate drepturile rezervate.</span>
      </div>
    </div>
  </footer>
);

/* ---------- Floating call / WhatsApp ---------- */
const FloatingActions = () => (
  <div className="floating-actions">
    <a
      className="fab fab-whatsapp"
      href={`https://wa.me/${company.whatsapp}`}
      target="_blank"
      rel="noreferrer"
      aria-label="Scrie-ne pe WhatsApp"
      title="Scrie-ne pe WhatsApp"
    >
      <MessageCircle size={24} />
    </a>
    <a
      className="fab fab-phone"
      href={`tel:${company.phoneHref}`}
      aria-label="Sună-ne acum"
      title="Sună-ne acum"
    >
      <Phone size={22} />
    </a>
  </div>
);

/* ---------- Layout ---------- */
const Layout = () => (
  <>
    <ScrollToTop />
    <Header />
    <main>
      <Outlet />
    </main>
    <Footer />
    <FloatingActions />
  </>
);

export default Layout;
